/**
 * Cognito Hosted UI sign-in via the OAuth 2.0 Authorization Code flow with
 * PKCE. The SPA is a public client (no client secret) so PKCE is required;
 * tokens are kept in memory only (Requirement 8.3).
 *
 * The flow:
 *   1. `beginLogin()` generates a PKCE verifier, computes the SHA-256
 *      challenge, stashes both the verifier + the requested `state` in
 *      `sessionStorage`, and redirects the browser to the Hosted UI
 *      `/oauth2/authorize` endpoint.
 *   2. The Hosted UI redirects back to the SPA's configured `redirectUri`
 *      with `?code=...&state=...`.
 *   3. `completeLogin()` reads the code, validates the state, posts the
 *      verifier to `/oauth2/token`, and returns the parsed token set.
 *   4. The token set is held in module memory by the AuthContext; nothing
 *      lands in `localStorage`.
 *
 * Only the ID token claims are inspected by the SPA — the access token is
 * sent on every API call as `Authorization: Bearer ...`. JWT signatures are
 * verified by API Gateway's Cognito authorizer; the SPA only decodes the ID
 * token to read group claims for route guards (Requirement 8.4), not to
 * trust them as an authorization decision.
 *
 * Validates: Requirements 8.1, 8.3, 8.4
 */

/** Static configuration for the Cognito Hosted UI client. */
export interface CognitoConfig {
  /** Hosted UI domain, e.g. `https://tcvs-dev.auth.us-east-1.amazoncognito.com`. */
  hostedUiDomain: string;
  /** The user-pool app client id. */
  clientId: string;
  /** Where Hosted UI redirects after sign-in / sign-out. Must match the app client config. */
  redirectUri: string;
  /** Scopes to request. `openid` is required; `profile` exposes group claims. */
  scopes?: string[];
  /**
   * Optional Cognito Hosted UI provider name to use for the primary sign-in
   * button, e.g. `EntraID`. When omitted, the app falls back to the Hosted UI
   * default/provider picker.
   */
  preferredIdentityProvider?: string;
}

/** Tokens returned by the Hosted UI `/oauth2/token` endpoint. */
export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds when the access token stops being accepted. */
  expiresAt: number;
}

/** Decoded claims from a Cognito ID token. Only the fields the SPA reads. */
export interface IdTokenClaims {
  sub: string;
  email?: string;
  /** Cognito groups claim. Present when the user is in at least one group. */
  groups: readonly string[];
  exp: number;
}

const STORAGE_KEY_VERIFIER = 'tcvs.pkce.verifier';
const STORAGE_KEY_STATE = 'tcvs.oauth.state';
/**
 * localStorage key for the persisted Cognito **refresh token**. This is the ONE
 * credential we persist (survives refresh AND new tabs); the id/access tokens
 * stay in memory only. Persisting the refresh token is the documented tradeoff
 * for not re-prompting on every load — it renews the session via a plain
 * token-endpoint call ({@link refreshTokens}), never an authorize redirect, so
 * it cannot cause a redirect loop.
 */
const STORAGE_KEY_REFRESH = 'tcvs.auth.refreshToken';

/**
 * Kick off the Hosted UI sign-in. This call NEVER returns — the browser
 * navigates to the authorize endpoint. Tests should stub `window.location`.
 */
export interface BeginLoginOptions {
  /** Override the provider used for this login attempt. */
  identityProvider?: string;
}

export async function beginLogin(
  config: CognitoConfig,
  options: BeginLoginOptions = {},
): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await sha256Base64Url(verifier);
  const state = generateState();
  sessionStorage.setItem(STORAGE_KEY_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_KEY_STATE, state);

  const identityProvider = options.identityProvider ?? config.preferredIdentityProvider;
  window.location.assign(buildAuthorizeUrl(config, state, challenge, identityProvider));
}

export function buildAuthorizeUrl(
  config: CognitoConfig,
  state: string,
  codeChallenge: string,
  identityProvider?: string,
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: (config.scopes ?? ['openid', 'profile']).join(' '),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (identityProvider !== undefined && identityProvider !== '') {
    params.set('identity_provider', identityProvider);
  }
  return `${config.hostedUiDomain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Exchange the `?code=...` from the redirect for a token set. Returns
 * `null` when the URL does not carry an authorization code (e.g. the page
 * was loaded without a redirect). Throws when the state mismatch suggests
 * a CSRF attempt or when the token endpoint rejects the exchange.
 */
export async function completeLogin(
  config: CognitoConfig,
  url: string = window.location.href,
): Promise<CognitoTokens | null> {
  const parsed = new URL(url);
  const code = parsed.searchParams.get('code');
  if (code === null) return null;

  const returnedState = parsed.searchParams.get('state');
  const expectedState = sessionStorage.getItem(STORAGE_KEY_STATE);
  if (returnedState === null || expectedState === null || returnedState !== expectedState) {
    throw new Error('oauth_state_mismatch');
  }
  const verifier = sessionStorage.getItem(STORAGE_KEY_VERIFIER);
  if (verifier === null) {
    throw new Error('oauth_verifier_missing');
  }
  sessionStorage.removeItem(STORAGE_KEY_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEY_STATE);

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: verifier,
  });
  const response = await fetch(`${config.hostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error(`token_exchange_failed_${response.status}`);
  }
  const json = (await response.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  const tokens: CognitoTokens = {
    idToken: json.id_token,
    accessToken: json.access_token,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
  if (json.refresh_token !== undefined) tokens.refreshToken = json.refresh_token;
  return tokens;
}

/**
 * Renew a session from a stored refresh token via `grant_type=refresh_token`.
 * This is a plain POST to the token endpoint — NO Hosted UI / authorize redirect
 * — so it can never trigger a redirect loop. Used on load to restore a session
 * without re-prompting.
 *
 * Returns the new token set on success, or `null` when the refresh token is
 * expired / revoked / rejected (the caller should then clear it and fall back to
 * an interactive sign-in). Cognito does NOT issue a new refresh token on refresh,
 * so the existing one is carried forward onto the returned token set.
 */
export async function refreshTokens(
  config: CognitoConfig,
  refreshToken: string,
): Promise<CognitoTokens | null> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  });
  let response: Response;
  try {
    response = await fetch(`${config.hostedUiDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    // Network error — treat as "couldn't refresh" rather than throwing so the
    // caller degrades to the signed-out view instead of an error screen.
    return null;
  }
  if (!response.ok) return null;
  const json = (await response.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  const tokens: CognitoTokens = {
    idToken: json.id_token,
    accessToken: json.access_token,
    expiresAt: Date.now() + json.expires_in * 1000,
    // Cognito omits refresh_token on a refresh grant — carry the existing one.
    refreshToken: json.refresh_token ?? refreshToken,
  };
  return tokens;
}

/** Read the persisted refresh token, or `null` when none / storage unavailable. */
export function loadStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_REFRESH);
  } catch {
    return null;
  }
}

/** Persist (or, when empty/undefined, clear) the refresh token in localStorage. */
export function persistRefreshToken(token: string | undefined): void {
  try {
    if (token === undefined || token === '') {
      localStorage.removeItem(STORAGE_KEY_REFRESH);
    } else {
      localStorage.setItem(STORAGE_KEY_REFRESH, token);
    }
  } catch {
    /* storage unavailable (private mode / disabled) — silently no-op. */
  }
}

/** Remove any persisted refresh token (sign-out / invalid token). */
export function clearStoredRefreshToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_REFRESH);
  } catch {
    /* no-op */
  }
}

/**
 * Decode an ID token's payload section. Does NOT verify the signature —
 * API Gateway's Cognito authorizer is the source of truth for that. The
 * SPA only uses the decoded claims to choose which routes to expose
 * (Requirement 8.4 forbids server-side trust of these claims).
 */
export function decodeIdToken(idToken: string): IdTokenClaims {
  const parts = idToken.split('.');
  if (parts.length !== 3) {
    throw new Error('invalid_id_token');
  }
  const payloadSegment = parts[1];
  if (payloadSegment === undefined || payloadSegment.length === 0) {
    throw new Error('invalid_id_token');
  }
  const payload = base64UrlDecode(payloadSegment);
  const claims = JSON.parse(payload) as Record<string, unknown>;
  const sub = typeof claims['sub'] === 'string' ? (claims['sub'] as string) : '';
  const exp = typeof claims['exp'] === 'number' ? (claims['exp'] as number) : 0;
  const email = typeof claims['email'] === 'string' ? (claims['email'] as string) : undefined;
  const rawGroups = claims['cognito:groups'];
  let groups: string[] = [];
  if (Array.isArray(rawGroups)) {
    groups = rawGroups.filter((v): v is string => typeof v === 'string');
  } else if (typeof rawGroups === 'string' && rawGroups.length > 0) {
    groups = rawGroups
      .replace(/^\[|\]$/g, '')
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  const result: IdTokenClaims = { sub, groups, exp };
  if (email !== undefined) result.email = email;
  return result;
}

/** True iff the decoded token has all the listed groups in `cognito:groups`. */
export function hasGroups(claims: IdTokenClaims, required: readonly string[]): boolean {
  return required.every((g) => claims.groups.includes(g));
}

/** True iff the decoded token has any of the listed groups. */
export function hasAnyGroup(claims: IdTokenClaims, allowed: readonly string[]): boolean {
  return allowed.some((g) => claims.groups.includes(g));
}

/** Build the Hosted UI logout URL. The browser must navigate to it. */
export function buildLogoutUrl(config: CognitoConfig): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    // Cognito requires `logout_uri` to EXACTLY match one of the app client's
    // registered sign-out URLs. Those are the app BASE url (with trailing
    // slash) — e.g. `https://…/` — NOT the OAuth callback. Our `redirectUri` is
    // `${base}/callback`, so strip the callback segment to `${base}/`. Sending
    // the callback here makes Cognito reject `logout_uri` and complain that the
    // `redirect_uri` parameter is missing.
    logout_uri: postLogoutRedirect(config.redirectUri),
  });
  return `${config.hostedUiDomain}/logout?${params.toString()}`;
}

/**
 * Derive the post-logout redirect (a registered sign-out URL) from the OAuth
 * `redirectUri`. The Frontend stack registers the app base URL (`${base}/`) as
 * the sign-out URL while `redirectUri` is `${base}/callback`, so strip a trailing
 * `/callback`. Any other shape is returned unchanged.
 */
function postLogoutRedirect(redirectUri: string): string {
  return /\/callback\/?$/.test(redirectUri)
    ? redirectUri.replace(/\/callback\/?$/, '/')
    : redirectUri;
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function generateCodeVerifier(): string {
  // 32 random bytes → 43-char base64url, well within RFC 7636's 43–128.
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncodeBytes(bytes);
}

function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncodeBytes(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncodeBytes(new Uint8Array(digest));
}

function base64UrlEncodeBytes(bytes: Uint8Array): string {
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i] as number);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const b64 = padded + '='.repeat(padLen);
  const binary = atob(b64);
  // Convert binary string to UTF-8 string. JWT payloads are UTF-8 JSON.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
